import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import SEOHead, { articleSchema, breadcrumbSchema, organizationSchema, faqSchema } from "@/components/SEOHead";
import MarketingHeader from "@/components/MarketingHeader";
import MarketingFooter from "@/components/MarketingFooter";
import FaqBlock, { CtaBlock } from "@/components/FaqBlock";
import { SITE } from "@/lib/seoData";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API}/blog/posts/${slug}`, { withCredentials: false })
      .then((res) => setPost(res.data))
      .catch((e) => setError(e.response?.status === 404 ? "Article not found." : "Couldn't load article"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <MarketingHeader />
        <main className="flex-1 flex items-center justify-center">
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-400">Loading…</p>
        </main>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <SEOHead title="Article not found" path={`/blog/${slug}`} noindex />
        <MarketingHeader />
        <main className="flex-1 max-w-3xl mx-auto px-6 md:px-10 py-16 w-full text-center">
          <h1 className="text-3xl font-semibold tracking-tight text-zinc-900">Article not found</h1>
          <p className="text-sm text-zinc-500 mt-3">{error}</p>
          <Link to="/blog" className="inline-block mt-6 text-sm text-[#002FA7] underline">Back to blog</Link>
        </main>
        <MarketingFooter />
      </div>
    );
  }

  const path = `/blog/${post.slug}`;
  const schemas = [
    organizationSchema(),
    articleSchema({
      title: post.title,
      description: post.excerpt,
      slug: post.slug,
      datePublished: post.published_at,
      author: post.author || SITE.founder.name,
    }),
    breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blog" },
      { name: post.title, path },
    ]),
  ];
  if (post.faqs?.length) schemas.push(faqSchema(post.faqs));

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <SEOHead
        title={`${post.title} | BreakItDown`}
        description={post.excerpt}
        path={path}
        type="article"
        schemas={schemas}
      />
      <MarketingHeader />
      <main className="flex-1 max-w-3xl mx-auto px-6 md:px-10 py-12 md:py-16 w-full">
        <nav className="text-xs text-zinc-500 mb-6">
          <Link to="/blog" className="hover:text-zinc-900">Blog</Link>
          {" / "}
          <span className="text-zinc-700">{post.category || "Article"}</span>
        </nav>
        <div className="text-xs font-mono text-zinc-500 mb-3">
          {post.published_at?.slice(0, 10)} · by {post.author || SITE.founder.name}
        </div>
        <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-zinc-900 leading-tight mb-6">
          {post.title}
        </h1>
        <p className="text-base md:text-lg text-zinc-600 leading-relaxed">{post.excerpt}</p>

        <article
          className="prose prose-zinc max-w-none mt-10 prose-headings:font-semibold prose-headings:tracking-tight prose-a:text-[#002FA7]"
          dangerouslySetInnerHTML={{ __html: post.body_html || "" }}
        />

        {post.faqs?.length > 0 && <FaqBlock faqs={post.faqs} />}
        <CtaBlock />
      </main>
      <MarketingFooter />
    </div>
  );
}
